/** @jsxImportSource @emotion/react */
// based on https://github.com/ggascoigne/react-table-example
import React, { CSSProperties, PropsWithChildren, ReactElement, ReactNode, useEffect, Suspense, useMemo } from 'react';
import {
  Cell, CellProps, Column,
  HeaderGroup, HeaderProps, Hooks,
  Meta, SortingRule,
  TableOptions,
  useFilters,
  useFlexLayout,
  usePagination, useResizeColumns, useRowSelect, useSortBy,
  useTable,
} from 'react-table';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { colors } from 'components/theme';
import Skeleton from 'react-loading-skeleton';
import { animateScroll as scroll } from 'react-scroll/modules';
import { Link } from 'react-router-dom';
import CompanyLink from 'modules/auth/CompanyLink';
import RowCheckbox from './RowCheckbox';
import { gridSize } from '../theme/constants';
import TablePagination from './TablePagination';

export interface TableProps<T extends object = {}> {
  name?: string;
  columns: Array<Column<T>>;
  data: T[];
  isLoading?: boolean;
  updateQueryParams?: (args: any) => void;
  pageCount?: number;
  withCheckboxes?: boolean;
  page?: number | null;
  pageSize?: number | null;
  sortBy?: string | null;
  hasPagination?: boolean;
  onRowClick?: (row: T) => void;
  noResults?: React.ReactNode;
  getRowTo?: (row: T) => string | null | undefined;
  skeletonCount?: number;
}

export interface Table<T extends object = {}> extends TableOptions<T>, TableProps<T> {
  // onAdd?: (instance: TableInstance<T>) => MouseEventHandler
  // onDelete?: (instance: TableInstance<T>) => MouseEventHandler
  // onEdit?: (instance: TableInstance<T>) => MouseEventHandler
  // onClick?: (row: Row<T>) => void
}
interface TableSortLabelProps {
  active: boolean;
  direction: 'asc'|'desc';
  align?: string;
  children: ReactNode;
  [key: string]: any;
}

const TableSortIcon = React.memo(function ({direction}:{direction: 'asc'|'desc';}) {
  return (
    <svg
      viewBox="0 0 20 20"
      css={css`
    display: inline-block;
    vertical-align: middle;
    margin: 0px 4px;
    margin-bottom: 1px;
    fill: currentColor;
    width: 0.8em;
    height: 0.8em;
    display: inline-block;
    transform: rotate(${direction === 'desc' ? '0' : '180'}deg);
    transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms
`}
    >
      <path d="M2.58317883,8.42398712 L8.46135867,14.3541686 C8.89737201,14.7841817 9.46138927,14.9981883 10.0194063,14.9981883 C10.5774234,14.9981883 11.1314404,14.7841817 11.5574534,14.3541686 L17.4176327,8.44398773 C18.1941224,7.65729226 18.1941224,6.39259637 17.4176327,5.6059009 C17.0466291,5.2294304 16.5401478,5.0174658 16.0115897,5.0174658 C15.4830315,5.0174658 14.9765503,5.2294304 14.6055467,5.6059009 L10.009406,10.2400427 L5.39526486,5.58590028 C5.02346007,5.21092652 4.51727879,5 3.98922185,5 C3.4611649,5 2.95498362,5.21092652 2.58317883,5.58590028 C1.80560706,6.37215139 1.80560706,7.63773602 2.58317883,8.42398712 Z" id="Path" />
    </svg>
  );
});

const TableSortLabel = React.memo(({active, direction, align = 'left', children, ...rest}:TableSortLabelProps) => {

  const label = <div css={css`display: inline-block;`}>{children}</div>;

  return (
    <div
      css={css`
    cursor: pointer;
    width: 100%;
    user-select: none;
    display: inline-flex;
    align-items: center;
    justify-content: ${align === 'left'? 'flex-start' :'flex-end'};
    & :hover svg {
    opacity: ${active ? 0.7 : 0.3};
    }
    & svg {opacity: ${active ? 0.7 : 0};
`}
      {...rest}
    >
      {align === 'left' && label}
      <TableSortIcon direction={direction} />
      {align === 'right' && label}
    </div>
  );
});


const getStyles = <T extends object>(props: any, disableResizing = false, align = 'left') => [
  props,
  {
    className: align,
    style: {
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      // textAlign: align === 'right' ? 'right' : 'left',
      alignItems: 'center',
      display: 'flex',
    },
  },
];

const headerProps = <T extends object>(props: any, { column }: Meta<T, { column: HeaderGroup<T> }>) =>
  getStyles(props, column && column.disableResizing, column && column.align);

const cellProps = <T extends object>(props: any, { cell }: Meta<T, { cell: Cell<T> }>) =>
  getStyles(props, cell.column && cell.column.disableResizing, cell.column && cell.column.align);

// eslint-disable-next-line no-shadow
const selectionHook = (hooks: Hooks<any>) => {
  hooks.allColumns.push((columns) => [
    // Let's make a column for selection
    {
      id: '_selector',
      disableResizing: true,
      disableGroupBy: true,
      minWidth: 45,
      width: 45,
      maxWidth: 45,
      // The header can use the table's getToggleAllRowsSelectedProps method
      // to render a checkbox
      Header: ({ getToggleAllRowsSelectedProps }: HeaderProps<any>) => {
        const toggleAllRowsSelectedProps = getToggleAllRowsSelectedProps();
        return <RowCheckbox {...getToggleAllRowsSelectedProps()} />;
      },

      // The cell can use the individual row's getToggleRowSelectedProps method
      // to the render a checkbox
      Cell: ({ row }: CellProps<any>) => {
        return <RowCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    ...columns,
  ]);

  hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
    // fix the parent group of the selection button to not be resizable
    const selectionGroupHeader = headerGroups[0].headers[0];
    selectionGroupHeader.canResize = false;
  });
};

const hooks: any[] = [
  // useColumnOrder,
  // useFilters,
  // useGroupBy,
  useSortBy,
  // useExpanded,
  useFlexLayout,
  usePagination,
  // useResizeColumns,
  // useRowSelect,
  // selectionHook,
];

const TableWrapper = styled.div`
    flex: 1;
    position: relative;
    table {
        display: flex;
        flex-direction: column;
    }
    th {
          padding: ${gridSize()* 1}px ${gridSize()*2}px;
    }
    td {
         > * {
           padding: ${gridSize()* 1.5}px ${gridSize()*2}px;
           font-style: inherit;
           text-align: inherit;
           color: inherit;
           text-decoration: none;
           background-color: transparent;
           border: 0;
           outline: none;
           cursor: pointer;
           width: 100%;
           height: 100%;
           display: block;
           border-bottom: 1px solid ${colors.N30};
           align-items: center;
           display: flex;
        }
        padding: 0px;
    }
     td:first-of-type > *, th:first-of-type {
           padding-left: ${gridSize() * 1.5}px
    }
   
     td:last-of-type, th:last-of-type > * {
           padding-right: ${gridSize() * 1.5}px
    }
    
    td {
    }
    tbody {
    border-bottom: none;
    }
    thead {
     border-bottom: 2px solid ${colors.N40};
     position: sticky;
     top: 0;
     background: white;
     z-index: 1 ;
    }
    th {
     position: sticky !important;
     color: ${colors.heading()};
     top: 0;
     font-weight: bold;
     //font-size: 12px;
      font-size: 11px;
      text-transform: uppercase;

     :hover {
             background: ${colors.N10}
            }
    }
    
    tbody tr {
         :hover {
             
             background: ${colors.N10}
            }
            :hover .visible-on-row-hover {
              opacity: 1;
            }
            .visible-on-row-hover {
              opacity: 0;
            }
    }
`;

const SortBy= {
  encode: (array: SortingRule<any>[] | null) => {
    if (!array || array.length === 0) return null;
    const [{ id, desc }] = array;
    return `${id} ${desc? 'DESC' : 'ASC'}`;
  },
  decode: (string: string | null | undefined) => {
    if (!string) return [];
    const regexResult = /(.*) (DESC|ASC)$/i.exec(string);
    if (!regexResult) return [];
    const [match, id, direction] = regexResult;
    return [{ id, desc: direction === 'DESC'}];
  }
};


function Table<T extends object>(props: PropsWithChildren<Table<T>>): ReactElement {
  const { name, columns, updateQueryParams, pageCount: controlledPageCount, getRowTo, withCheckboxes, onRowClick, data, isLoading, hasPagination, manualPagination, noResults = 'Geen resultaten', skeletonCount = 6 } = props;

  // const [initialState, setInitialState] = useLocalStorage(`tableState:${name}`, {
  //   defaultPageSize: 50,
  // });
  const {page: controlledPage, pageSize: controlledPageSize, sortBy: controlledSortBy} = props;

  const [lastDataCount, setLastDataCount] = React.useState(0);
  useEffect(() => {
    if (data) {
      setLastDataCount(data.length);
    }
  }, [data]);

  const initialState = useMemo(() => {
    const sortByDecoded = SortBy.decode(controlledSortBy);
    return {
      // page: controlledPage,
      pageIndex: (controlledPage || 1) - 1,
      pageSize: controlledPageSize || 25,
      sortBy: sortByDecoded,
    };
  }, [controlledPage, controlledPageSize, controlledSortBy]);


  // const {page, pageSize } = query;

  // if(withCheckboxes) {
  //   hooks.push(selectionHook);
  // }

  const shouldRenderSkeleton = isLoading || !data;

  const renderSkeletonCount = lastDataCount || skeletonCount;

  const tableData = React.useMemo(
    () => (shouldRenderSkeleton ? Array(renderSkeletonCount).fill({}) : data),
    [shouldRenderSkeleton, renderSkeletonCount, data]
  );

  // console.log('tableData',tableData?.length);
  // console.log('shouldRenderSkeleton',shouldRenderSkeleton);

  const tableColumns = React.useMemo(
    () =>
      shouldRenderSkeleton
        ? columns.map((column) => ({
          ...column,
          Cell: ()=> (
            <div css={css`height: 100%; width: 100%; padding: ${gridSize()*1.44}px 0px; align-items: center; justify-content: ${column.align === 'right'? 'flex-end' : 'flex-start'}; display: flex;`}>
              <div style={{ width: '60%'}}><Skeleton /></div>
            </div>
          ),
        }))
        : columns,
    [shouldRenderSkeleton, columns]
  );


  const instance = useTable({
    ...props,
    data: tableData,
    columns: tableColumns,
    initialState,
    manualPagination, // Tell the usePagination
    disableSortRemove: true,
    // hook that we'll handle our own data fetching
    // This means we'll also have to provide our own
    // pageCount.
    pageCount: controlledPageCount,
    disableMultiSort: true,
    // initialState: { pageIndex: 2 },
  },
  ...hooks,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    selectedFlatRows,
    toggleRowSelected
  } = instance;

  // const debouncedState = useDebounce(state, 1000);
  //
  // useEffect(() => {
  //   const { sortBy, filters, pageSize, columnResizing, hiddenColumns } = debouncedState;
  //   const val = {
  //     sortBy,
  //     // filters,
  //     pageSize,
  //     // columnResizing,
  //     hiddenColumns,
  //   };
  //   setInitialState(val)
  // }, [setInitialState,debouncedState]);

  const { sortBy,
    // filters,
    pageSize, pageIndex } = state;

  useEffect(() => {
    scroll.scrollToTop({ containerId: 'tbody', duration: 200 });
  }, [controlledPage, controlledSortBy]);

  // Listen for changes in pagination and use the state to fetch our new data

  const sortByString = SortBy.encode(sortBy);

  React.useEffect(() => {
    if (updateQueryParams) {
      updateQueryParams({
        sortBy: sortByString,
        page: pageIndex + 1,
        pageSize });
    }
  }, [pageIndex, pageSize, sortByString]);


  // Render the UI for your table
  return (
    <>
      <TableWrapper>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => {
                  const style = {
                    textAlign: column.align ? column.align : 'left ',
                  } as CSSProperties;

                  return (
                    <th {...column.getHeaderProps(headerProps)}>
                      {column.canSort ? (
                        <TableSortLabel
                          align={column.align}
                          active={column.isSorted}
                          direction={column.isSortedDesc ? 'desc' : 'asc'}
                          {...column.getSortByToggleProps()}
                          style={style} // FIXME re-renders every time?
                          className='table-sort-label'
                        >
                          {column.render('Header')}
                        </TableSortLabel>
                      ) : (
                        <div
                          style={style}
                        >
                          {column.render('Header')}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} id="tbody">
            <Suspense fallback={<div>Loading tbody ...</div>}>
              {page.map((row, i) => {
                prepareRow(row);
                const extraProps: {
                  onClick?: (row: any) => void;
                  style?: CSSProperties;
                } = {
                };
                // if(selectedFlatRows.length>0){
                //   extraProps.onClick= () => row.toggleRowSelected();
                // }
                const rowProps = row.getRowProps();
                if (onRowClick) {
                  // extraProps.onClick = (event) => {console.log('event.type',event.type); onRowClick(row.original)};
                  // extraProps.style = {...rowProps.style, cursor: 'pointer'};
                }

                const rowData = row.original as any;
                let rowTo:string | null | undefined;
                if (getRowTo) {
                  rowTo = getRowTo(rowData);
                }
                return (
                  <tr
                    {...rowProps}
                    {...extraProps}
                  >
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps(cellProps)}>
                          {(cell.column.noLink || !rowTo)? <div>{cell.render('Cell')}</div> : (
                            <CompanyLink
                              style={{
                                justifyContent: cell.column.align === 'right' ? 'flex-end' : 'flex-start',
                                alignItems: 'center',
                                display: 'flex'}}
                              to={rowTo}
                            >
                              {cell.render('Cell')}
                            </CompanyLink>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </Suspense>
            {!shouldRenderSkeleton && (!data || data.length === 0) && (
            <tr css={css`display: flex;`}>
              <td css={css`flex: 1;`}><div>{noResults}</div></td>
            </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>
      {hasPagination && <TablePagination instance={instance} />}
    </>
  );
}

// @ts-ignore
Table.defaultProps = {
  disableSortRemove: true,
  hasPagination: false,
  manualPagination: true,
  skeletonCount: 6,
};

// export default React.memo( (props) => {
//   useWhyDidYouUpdate('Table',props)
//   return <Table {...props}/>
// }) as typeof Table;

export default React.memo(Table) as typeof Table;
// export default Table as typeof Table;
// export default React.memo( Table, (prevProps, nextProps) => {
//   if(_.isEqual(prevProps.data, nextProps.data))
//   }
// ) as typeof Table;
